import mapboxgl, { type MapOptions, type Map as Mapbox } from "mapbox-gl";
import { useEffect, useRef, useState } from "react";
import "mapbox-gl/dist/mapbox-gl.css";
import { DEFAULT_MAP_ZOOM } from "@/utils/geoBounds";

export const useMap = (options?: Omit<MapOptions, "container">) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const hasInitialized = useRef(false);
  const [map, setMap] = useState<Mapbox | null>(null);

  // Initialize map
  useEffect(() => {
    if (hasInitialized.current || !ref.current) return;

    if (!import.meta.env.VITE_MAPBOX_TOKEN) {
      console.error("VITE_MAPBOX_TOKEN must be defined");
      return;
    }

    mapboxgl.accessToken = import.meta.env.VITE_MAPBOX_TOKEN;

    try {
      const mapInstance = new mapboxgl.Map({
        container: ref.current,
        // style: "mapbox://styles/lcdigital/cmb13hg1800ea01se1a3bfwl6",
        style: "mapbox://styles/lcdigital/cmb13ddi800gv01sdh5ev0b4s",
        center: options?.center || [41.98, 2.979],
        zoom: DEFAULT_MAP_ZOOM,
        attributionControl: false,
        ...options,
      });

      mapInstance.addControl(new mapboxgl.AttributionControl({ compact: true }));

      // Defer exposing the map instance until the style and globe projection
      // are fully loaded. Calling fitBounds with large asymmetric padding
      // before the globe projection initializes corrupts its WebGL rendering
      // matrices, leaving an unrendered circular area on the canvas.
      mapInstance.on("load", () => setMap(mapInstance));
    } catch (error) {
      console.error("Failed to initialize map:", error);
    }

    hasInitialized.current = true;
  }, [options]);

  return {
    ref,
    map,
  };
};
